'use client';

import * as React from 'react';
import Link from 'next/link';
import { Music, MapPin, Calendar, Clock, Star, Search, Compass } from 'lucide-react';
import type { CityCode } from '@loci/city-editions';
import { cn } from '@/components/atoms/primitives/utils';
import { Badge } from '@/components/atoms/Badge';
import { CategoryBadge, type BadgeCategory as LociBadgeCategory } from '@loci/ui';
import { useSearchSuggestions } from '@/hooks/useSearchSuggestions';
import { useRecentSearches } from '@/hooks/useRecentSearches';
import { usePopularSearches } from '@/hooks/usePopularSearches';
import { useDiscoverHref } from '@/hooks/useDiscoverHref';
import { emitSearchEvent } from '@/lib/telemetry/searchEvents';
import type { SearchResultCategory } from '@/lib/telemetry/searchEvents';
import type { Event } from '@/types/event';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface SearchSuggestionItem {
  type: 'artist' | 'venue' | 'location' | 'event' | 'recent' | 'popular';
  value: string;
  label: string;
  category?: string;
  /**
   * Only present when `type === 'location'` (WEB-965) — see
   * `classifyLocationSuggestionKind` in `@/lib/neighborhoods/clt-slugs`.
   * `SearchSheet.handleSelect` uses it (with a defensive fallback) to route
   * a city suggestion to the unfiltered city list instead of a neighborhood
   * filter that would match nothing.
   */
  locationKind?: 'city' | 'neighborhood';
}

/**
 * Props for the presentational autocomplete overlay.
 *
 * On desktop (≥1024px), renders as an absolutely-positioned dropdown.
 * The parent must have `position: relative` so the dropdown anchors
 * correctly beneath the search input.
 */
export interface SearchAutocompleteViewProps {
  isOpen: boolean;
  suggestions: SearchSuggestionItem[];
  recentSearches: string[];
  hasRecentSearches: boolean;
  /**
   * FRE-824: curated static popular searches surfaced on the empty-state
   * view (no active query). Always render alongside recents when present;
   * caller controls visibility via {@link showPopular}.
   */
  popularSearches?: readonly string[];
  /**
   * FRE-824: whether to render the Popular section. The view stays purely
   * presentational — the connected component decides this based on whether
   * the query is empty.
   */
  showPopular?: boolean;
  /**
   * FRE-824: href for the "Try Discover" CTA shown when both the query and
   * the recents list are empty. `null` hides the CTA — the connected
   * component derives this from `cityCode` via `useDiscoverHref` (WEB-1231).
   */
  discoverHref?: string | null;
  isLoading?: boolean;
  /**
   * FRE-821: quiet inline "Slow down. Searching again in N s" hint shown
   * when the backend returned 429 for the current query. The countdown is
   * driven locally so the timer can tick without forcing the hook to
   * re-render once per second. `null` hides the hint entirely.
   */
  rateLimitNotice?: { retryAfterSeconds: number } | null;
  /**
   * FRE-829: text announced by the visually-hidden `aria-live="polite"`
   * results-summary region (e.g. "3 results" / "No results"). The connected
   * component derives this from the debounced (settled) query+suggestions
   * state so screen readers don't fire on every keystroke. Empty string
   * suppresses the announcement (initial open with no query, while loading,
   * or with a 1-char query that's below the suggestion threshold).
   */
  resultsAnnouncement?: string;
  onSelect: (value: string, type: string, locationKind?: 'city' | 'neighborhood') => void;
  onClearRecent: () => void;
  onDismiss: () => void;
  activeIndex: number;
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Group suggestions by category — single source of truth for render order */
function groupSuggestions(
  suggestions: SearchSuggestionItem[],
): Record<string, SearchSuggestionItem[]> {
  const groups: Record<string, SearchSuggestionItem[]> = {};
  for (const suggestion of suggestions) {
    const cat = suggestion.category ?? 'Other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(suggestion);
  }
  return groups;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const TYPE_ICONS: Record<
  SearchSuggestionItem['type'],
  React.ComponentType<{ className?: string }>
> = {
  artist: Music,
  venue: MapPin,
  location: MapPin,
  event: Calendar,
  recent: Clock,
  popular: Star,
};

const BADGE_CATEGORIES: ReadonlyArray<LociBadgeCategory> = [
  'music',
  'sports',
  'food',
  'tech',
  'art',
  'drinks',
];

/**
 * Maps a free-form category string to a `@loci/ui` `BadgeCategory` if recognised.
 * Returns `null` when no canonical category matches — caller should render a
 * generic `Badge` instead of `CategoryBadge`. Uses exact lowercase match so
 * non-LOCI category labels (e.g. "Artists", "Venues", "Events") fall through
 * to the generic Badge path and don't accidentally pick up category styling.
 *
 * `@loci/ui` `BadgeCategory` is still the legacy six (includes retired `tech`).
 * Live wire slugs use `science-tech` (WEB-912); map that onto the existing
 * tech badge until the DS closes the ten. The four net-new categories have
 * no DS badge and correctly fall through to generic `Badge`.
 */
function toLociBadgeCategory(category: string | undefined): LociBadgeCategory | null {
  if (!category) return null;
  const lower = category.toLowerCase();
  if (lower === 'science-tech') return 'tech';
  return BADGE_CATEGORIES.find((c) => c === lower) ?? null;
}

/**
 * FRE-822: map the wider suggestion `type` set ({@link SearchSuggestionItem}) to
 * the narrower telemetry category set ({@link SearchResultCategory}). Returns
 * `null` for `'recent'` / `'popular'` because those rows fire dedicated
 * events (`search_recent_used`) instead of `search_result_clicked`.
 */
function toTelemetryCategory(type: string): SearchResultCategory | null {
  switch (type) {
    case 'artist':
    case 'venue':
    case 'event':
    case 'location':
      return type;
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/*  SuggestionButton — shared row renderer                             */
/* ------------------------------------------------------------------ */

interface SuggestionButtonProps {
  id: string;
  isActive: boolean;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  type: string;
  category?: string;
  /** Only meaningful when `type === 'location'` (WEB-965). */
  locationKind?: 'city' | 'neighborhood';
  onSelect: (value: string, type: string, locationKind?: 'city' | 'neighborhood') => void;
}

function SuggestionButton({
  id,
  isActive,
  icon: Icon,
  label,
  value,
  type,
  category,
  locationKind,
  onSelect,
}: SuggestionButtonProps): React.ReactElement {
  return (
    <button
      id={id}
      type="button"
      role="option"
      aria-selected={isActive}
      className={cn(
        'flex w-full items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer min-h-[44px] desktop:min-h-0 transition-colors text-left',
        'hover:bg-accent focus:bg-accent focus-visible:outline-2 focus-visible:outline-ring',
        isActive && 'bg-accent text-accent-foreground',
      )}
      data-testid="suggestion-item"
      // WEB-965: only forward a third argument when the row actually has a
      // `locationKind` — keeps the call shape `(value, type)` for every
      // other suggestion type, so callers/tests asserting an exact 2-arg
      // `onSelect` call aren't broken by an explicit `undefined` third arg.
      onClick={() => (locationKind ? onSelect(value, type, locationKind) : onSelect(value, type))}
    >
      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="text-sm truncate text-foreground">{label}</span>
      {category &&
        (() => {
          const lociCategory = toLociBadgeCategory(category);
          return lociCategory ? (
            <CategoryBadge
              category={lociCategory}
              variant="outlined"
              size="xs"
              className="ml-auto shrink-0"
            />
          ) : (
            <Badge className="ml-auto shrink-0">{category}</Badge>
          );
        })()}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Skeleton loader                                                    */
/* ------------------------------------------------------------------ */

function SkeletonRow(): React.ReactElement {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 min-h-[44px] animate-pulse">
      <div className="size-4 shrink-0 rounded bg-muted" />
      <div className="h-4 flex-1 rounded bg-muted" />
      <div className="h-4 w-16 shrink-0 rounded bg-muted" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Rate-limit notice — degrade-render only (FRE-821)                  */
/* ------------------------------------------------------------------ */

/**
 * Quiet inline countdown shown when the backend returned 429 for the
 * current query. Driven locally so the per-second tick does not propagate
 * up through {@link useSearchSuggestions} and re-render the entire
 * autocomplete tree.
 *
 * Resets the local timer whenever the parent hands down a new
 * `retryAfterSeconds` value — a fresh 429 mid-cooldown should re-extend
 * the countdown, not silently keep the old one ticking.
 */
function RateLimitNotice({
  retryAfterSeconds,
}: {
  readonly retryAfterSeconds: number;
}): React.ReactElement {
  const [remaining, setRemaining] = React.useState<number>(retryAfterSeconds);

  React.useEffect(() => {
    setRemaining(retryAfterSeconds);
    if (retryAfterSeconds <= 0) return;
    const interval = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [retryAfterSeconds]);

  if (remaining <= 0) return <></>;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="rate-limit-notice"
      className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground"
    >
      <span>Slow down. Searching again in {remaining}s</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Presentational component — used by Storybook & tests              */
/* ------------------------------------------------------------------ */

export function SearchAutocompleteView({
  isOpen,
  suggestions,
  recentSearches,
  hasRecentSearches,
  popularSearches = [],
  showPopular = false,
  discoverHref = null,
  isLoading = false,
  rateLimitNotice = null,
  resultsAnnouncement = '',
  onSelect,
  onClearRecent,
  onDismiss,
  activeIndex,
  className,
}: SearchAutocompleteViewProps): React.ReactElement | null {
  const grouped = React.useMemo(() => groupSuggestions(suggestions), [suggestions]);
  const hasPopular = showPopular && popularSearches.length > 0;
  // Empty-state CTA appears only when there's nothing else to surface in
  // the listbox: no query (so no live suggestions), no recents. Popular
  // may still render alongside it (per AC bullet 3).
  const showDiscoverCta =
    !isLoading &&
    !hasRecentSearches &&
    suggestions.length === 0 &&
    discoverHref !== null &&
    discoverHref.length > 0;

  if (!isOpen) return null;

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onDismiss();
    }
  };

  let renderIndex = 0;

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 bg-background/95 backdrop-blur-sm transition-transform duration-200',
        'desktop:absolute desktop:top-full desktop:inset-x-0 desktop:bottom-auto desktop:max-h-[400px] desktop:rounded-lg desktop:border desktop:shadow-lg desktop:bg-background desktop:backdrop-blur-none',
        className,
      )}
      data-testid="search-autocomplete"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {/* FRE-829: visually-hidden polite live region that announces the
          settled results count to assistive tech. Always rendered so the
          live region itself is stable across query changes — assistive tech
          only re-reads the contents when the text node changes. The
          connected component derives `resultsAnnouncement` from the
          debounced query so this fires once per finalised query (matches
          the `search_query_typed` cadence), not on every keystroke. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid="search-results-announcement"
        className="sr-only"
      >
        {resultsAnnouncement}
      </div>

      <div className="overflow-y-auto max-h-[calc(100vh-120px)] desktop:max-h-[400px] p-4 desktop:p-2">
        {/* Rate-limit notice — FRE-821 quiet degrade UI, rendered above
            everything else so the user sees it even while the existing
            (preserved) list is shown beneath. Strictly degrade-render path —
            FRE-822 telemetry hooks live elsewhere in this component. */}
        {rateLimitNotice && (
          <RateLimitNotice retryAfterSeconds={rateLimitNotice.retryAfterSeconds} />
        )}

        {/* Loading state */}
        {isLoading && (
          <div data-testid="loading-state">
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </div>
        )}

        {/* Recent Searches Header — outside listbox for valid ARIA structure.
            FRE-829 a11y wave will wire the `<h3 id>` to the matching
            `role="group"` below via `aria-labelledby`; keep both ids stable. */}
        {!isLoading && hasRecentSearches && (
          <div
            className="flex items-center justify-between mb-2"
            data-testid="recent-searches-section"
          >
            <h3
              id="recent-searches-label"
              className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
            >
              Recent Searches
            </h3>
            <button
              type="button"
              onClick={onClearRecent}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors min-h-[44px] desktop:min-h-0 min-w-[44px] desktop:min-w-0 px-2"
              data-testid="clear-recent-button"
              aria-label="Clear recent searches"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Popular Searches Header — outside listbox for valid ARIA structure.
            FRE-829 will wire `aria-labelledby="search-popular-heading"` onto
            the matching `role="group"` below; keep the id stable. */}
        {!isLoading && hasPopular && (
          <div
            className={cn('flex items-center mb-2', hasRecentSearches && 'mt-2')}
            data-testid="search-popular"
          >
            <h3
              id="search-popular-heading"
              className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
            >
              Popular searches
            </h3>
          </div>
        )}

        {/* Listbox — only contains role="option" and role="group" children */}
        {!isLoading && (
          <div id="search-autocomplete-listbox" role="listbox" aria-label="Search suggestions">
            {/* Recent search options. `data-testid="search-recents"` is the
                FRE-829 a11y target — keep stable so a11y wiring lands cleanly. */}
            {hasRecentSearches && (
              <div
                role="group"
                aria-labelledby="recent-searches-label"
                className="mb-4"
                data-testid="search-recents"
              >
                {recentSearches.map((query) => {
                  const currentIndex = renderIndex;
                  renderIndex++;
                  return (
                    <SuggestionButton
                      key={`suggestion-${currentIndex}`}
                      id={`suggestion-${currentIndex}`}
                      isActive={currentIndex === activeIndex}
                      icon={Clock}
                      label={query}
                      value={query}
                      type="recent"
                      onSelect={onSelect}
                    />
                  );
                })}
              </div>
            )}

            {/* Popular search options. Renders after recents, before suggestions.
                Keyboard navigation order is enforced by the parent flatItems
                memo (recents → popular → suggestions). */}
            {hasPopular && (
              <div
                role="group"
                aria-labelledby="search-popular-heading"
                className="mb-4"
                data-testid="search-popular-list"
              >
                {popularSearches.map((query) => {
                  const currentIndex = renderIndex;
                  renderIndex++;
                  return (
                    <SuggestionButton
                      key={`suggestion-${currentIndex}`}
                      id={`suggestion-${currentIndex}`}
                      isActive={currentIndex === activeIndex}
                      icon={Star}
                      label={query}
                      value={query}
                      type="popular"
                      onSelect={onSelect}
                    />
                  );
                })}
              </div>
            )}

            {/* Grouped Suggestions — header outside group for valid ARIA */}
            {Object.entries(grouped).map(([category, items]) => (
              <div key={category} data-testid="suggestions-section" className="mb-4 last:mb-0">
                <div
                  id={`section-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className="sticky top-0 bg-background/95 backdrop-blur-sm px-4 py-2 text-sm font-medium text-muted-foreground border-b"
                >
                  {category}
                </div>
                <div role="group" aria-label={category}>
                  {items.map((suggestion) => {
                    const currentIndex = renderIndex;
                    renderIndex++;
                    return (
                      <SuggestionButton
                        key={`suggestion-${currentIndex}`}
                        id={`suggestion-${currentIndex}`}
                        isActive={currentIndex === activeIndex}
                        icon={TYPE_ICONS[suggestion.type] ?? Search}
                        label={suggestion.label}
                        value={suggestion.value}
                        type={suggestion.type}
                        category={suggestion.category}
                        locationKind={suggestion.locationKind}
                        onSelect={onSelect}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty-state CTA — shown when nothing else is surfaceable.
            Renders alongside the Popular section above (per AC bullet 3:
            "Empty state shows Popular section + a 'Try Discover' CTA"). */}
        {showDiscoverCta && (
          <div
            className="flex flex-col items-center justify-center gap-3 py-8 text-muted-foreground"
            data-testid="empty-state"
          >
            <Search className="size-8" aria-hidden="true" />
            <p className="text-sm">Not sure what to search?</p>
            <Link
              href={discoverHref as string}
              data-testid="try-discover-cta"
              onClick={onDismiss}
              className={cn(
                'inline-flex items-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground',
                'hover:bg-accent hover:text-accent-foreground transition-colors min-h-[44px] desktop:min-h-0',
                'focus-visible:outline-2 focus-visible:outline-ring',
              )}
            >
              <Compass className="size-4" aria-hidden="true" />
              Try Discover
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Connected component — wires hooks and delegates to View           */
/* ------------------------------------------------------------------ */

export interface SearchAutocompleteProps {
  isOpen: boolean;
  isLoading?: boolean;
  query: string;
  events: Event[];
  /**
   * FRE-824: active city — used to compose the "Try Discover" CTA href via
   * `useDiscoverHref` (WEB-1231 / R25). Optional so tests / Storybook can
   * render the component without plumbing a city; the CTA is hidden when
   * the city is absent.
   */
  cityCode?: CityCode;
  onSelect: (value: string, type: string, locationKind?: 'city' | 'neighborhood') => void;
  onDismiss: () => void;
  /** Callback with the active item's DOM id for parent to set aria-activedescendant on its input */
  onActiveItemIdChange?: (id: string | undefined) => void;
  className?: string;
}

export function SearchAutocomplete({
  isOpen,
  isLoading,
  query,
  events,
  cityCode,
  onSelect,
  onDismiss,
  onActiveItemIdChange,
  className,
}: SearchAutocompleteProps): React.ReactElement | null {
  const { getFilteredSuggestions, fetchApiSuggestions, apiSuggestions } =
    useSearchSuggestions(events);
  const { getRecentQueries, clearRecentSearches, hasRecentSearches } = useRecentSearches();
  const popularSearches = usePopularSearches();
  const [activeIndex, setActiveIndex] = React.useState<number>(-1);

  React.useEffect(() => {
    fetchApiSuggestions(query);
  }, [fetchApiSuggestions, query]);

  // WEB-1215: call this during render. getFilteredSuggestions reads the
  // API list from a ref and keeps a stable identity, so memoizing it on
  // the callback keeps the empty pre-fetch list after the response lands.
  const suggestions = getFilteredSuggestions(query);
  const recentSearches = React.useMemo(() => getRecentQueries(), [getRecentQueries]);

  // FRE-824: Popular surfaces only when no active query (matches the
  // "empty + has recents" and "empty + no recents" AC paths). Trim
  // matches the `search_query_typed` debounce semantics so whitespace
  // doesn't accidentally hide the popular list.
  const showPopular = query.trim().length === 0;

  // FRE-824: derive the "Try Discover" CTA href from cityCode. `null` hides
  // the CTA — the View checks this alongside `hasRecentSearches`/suggestions
  // to decide whether to render the CTA block.
  // WEB-1231 / R25: signed-in viewers stay on `app.*`.
  const buildDiscoverHref = useDiscoverHref(cityCode);
  const discoverHref = React.useMemo<string | null>(
    () => (cityCode ? buildDiscoverHref() : null),
    [buildDiscoverHref, cityCode],
  );

  // Build flat items list in grouped render order so keyboard selection
  // matches the visual order (recents → popular → suggestions by group).
  // FRE-824: insert popular AFTER recents and BEFORE suggestions to match
  // the visual stacking in the View.
  const flatItems = React.useMemo(() => {
    const items: Array<{
      value: string;
      type: string;
      locationKind?: 'city' | 'neighborhood';
    }> = [];
    for (const recentQuery of recentSearches) {
      items.push({ value: recentQuery, type: 'recent' });
    }
    if (showPopular) {
      for (const popularQuery of popularSearches) {
        items.push({ value: popularQuery, type: 'popular' });
      }
    }
    const groups = groupSuggestions(suggestions);
    for (const groupItems of Object.values(groups)) {
      for (const suggestion of groupItems) {
        items.push({
          value: suggestion.value,
          type: suggestion.type,
          locationKind: suggestion.locationKind,
        });
      }
    }
    return items;
  }, [recentSearches, showPopular, popularSearches, suggestions]);

  /* ---------------------------------------------------------------- */
  /*  FRE-822 telemetry                                                */
  /* ---------------------------------------------------------------- */

  // Fire `search_query_typed` exactly once per finalized query. The
  // underlying `useSearchSuggestions` hook already debounces the request
  // at 150ms; we layer a tiny 300ms debounce on top so back-to-back
  // keystrokes don't each produce their own event. We deliberately count
  // results from the *rendered* list (`suggestions`) rather than the API
  // response so a 429 / error / fallback path still reports the right
  // number of rows the user actually saw.
  //
  // PRIVACY: only queryLength + resultCount cross the boundary — never
  // the raw query string. The `emitSearchEvent` schema enforces this at
  // the type layer.
  const lastReportedQueryRef = React.useRef<string | null>(null);
  const queryTypedTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const resultsCountRef = React.useRef<number>(0);
  resultsCountRef.current = suggestions.length;
  React.useEffect(() => {
    if (!isOpen) return undefined;
    // Match the suggestion API's short-circuit: whitespace and 1-char inputs
    // aren't finalized queries, so they shouldn't produce telemetry.
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 2) return undefined;
    if (lastReportedQueryRef.current === normalizedQuery) return undefined;

    if (queryTypedTimerRef.current) clearTimeout(queryTypedTimerRef.current);
    queryTypedTimerRef.current = setTimeout(() => {
      lastReportedQueryRef.current = normalizedQuery;
      emitSearchEvent({
        type: 'search_query_typed',
        queryLength: normalizedQuery.length,
        resultCount: resultsCountRef.current,
      });
    }, 300);

    return () => {
      if (queryTypedTimerRef.current) {
        clearTimeout(queryTypedTimerRef.current);
        queryTypedTimerRef.current = null;
      }
    };
  }, [query, isOpen]);

  // Reset the "last reported" memo whenever the sheet closes so the next
  // open cycle's first finalized query fires fresh.
  React.useEffect(() => {
    if (!isOpen) {
      lastReportedQueryRef.current = null;
    }
  }, [isOpen]);

  // Intercept selection so we can fire `search_result_clicked` /
  // `search_recent_used` / `search_popular_clicked` BEFORE handing off to
  // the caller-supplied `onSelect` (which closes the sheet, runs
  // router.push, etc.). Position is derived from `flatItems` so it matches
  // the visually-rendered order.
  const handleSelect = React.useCallback(
    (value: string, type: string, locationKind?: 'city' | 'neighborhood') => {
      const position = flatItems.findIndex((item) => item.value === value && item.type === type);
      // WEB-965: only forward a third argument when present — keeps the
      // call shape `(value, type)` for every suggestion type but `location`,
      // matching `SuggestionButton`'s onClick and avoiding an explicit
      // `undefined` third arg that would break a strict 2-arg
      // `toHaveBeenCalledWith(value, type)` assertion in existing tests.
      const forwardSelect = (): void => {
        if (locationKind) {
          onSelect(value, type, locationKind);
        } else {
          onSelect(value, type);
        }
      };
      // Skip telemetry when index resolution fails — coercing to 0 would
      // misattribute the click to the first row. Selection still proceeds.
      if (position < 0) {
        forwardSelect();
        return;
      }
      if (type === 'recent') {
        emitSearchEvent({ type: 'search_recent_used', position });
      } else if (type === 'popular') {
        // FRE-824: popular searches get their own event for funnel analysis
        // (cold-empty users vs. returning users with recents).
        emitSearchEvent({ type: 'search_popular_clicked', position });
      } else {
        const telemetryCategory = toTelemetryCategory(type);
        if (telemetryCategory) {
          emitSearchEvent({
            type: 'search_result_clicked',
            category: telemetryCategory,
            position,
          });
        }
      }
      forwardSelect();
    },
    [flatItems, onSelect],
  );

  const totalItems = flatItems.length;

  /* ---------------------------------------------------------------- */
  /*  FRE-829 a11y — results-count announcement (aria-live polite)     */
  /* ---------------------------------------------------------------- */

  // Debounced text fed into the visually-hidden aria-live region. Matches
  // the 300ms cadence of the FRE-822 `search_query_typed` event so screen
  // readers only hear "N results" once the query has settled — not on
  // every keystroke. Resets to empty when the sheet closes so the next
  // open cycle starts silent.
  const [resultsAnnouncement, setResultsAnnouncement] = React.useState<string>('');
  const announceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => {
    if (announceTimerRef.current) {
      clearTimeout(announceTimerRef.current);
      announceTimerRef.current = null;
    }
    if (!isOpen) {
      setResultsAnnouncement('');
      return undefined;
    }
    // Mirror the suggestion API's short-circuit — whitespace and 1-char
    // inputs aren't finalised queries and shouldn't announce.
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 2) {
      setResultsAnnouncement('');
      return undefined;
    }
    if (isLoading) {
      // Don't pre-empt the settled count with a stale value while loading.
      return undefined;
    }
    announceTimerRef.current = setTimeout(() => {
      const count = suggestions.length;
      setResultsAnnouncement(
        count === 0 ? 'No results' : `${count} result${count === 1 ? '' : 's'}`,
      );
    }, 300);
    return () => {
      if (announceTimerRef.current) {
        clearTimeout(announceTimerRef.current);
        announceTimerRef.current = null;
      }
    };
  }, [query, isOpen, isLoading, suggestions.length]);

  // Reset active index when query or open state changes
  React.useEffect(() => {
    setActiveIndex(-1);
  }, [query, isOpen]);

  // Notify parent of active item ID for aria-activedescendant on the input
  React.useEffect(() => {
    const id = activeIndex >= 0 ? `suggestion-${activeIndex}` : undefined;
    onActiveItemIdChange?.(id);
  }, [activeIndex, onActiveItemIdChange]);

  // Scroll active item into view
  React.useEffect(() => {
    if (activeIndex < 0) return;
    const el = document.getElementById(`suggestion-${activeIndex}`);
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  // Keyboard handler
  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          setActiveIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          setActiveIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
          break;
        }
        case 'Enter': {
          e.preventDefault();
          const item = flatItems[activeIndex];
          if (item) {
            handleSelect(item.value, item.type, item.locationKind);
          }
          break;
        }
        case 'Escape': {
          e.preventDefault();
          onDismiss();
          break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeIndex, totalItems, flatItems, handleSelect, onDismiss]);

  return (
    <SearchAutocompleteView
      isOpen={isOpen}
      suggestions={suggestions}
      recentSearches={recentSearches}
      hasRecentSearches={hasRecentSearches}
      popularSearches={popularSearches}
      showPopular={showPopular}
      discoverHref={discoverHref}
      isLoading={isLoading}
      rateLimitNotice={apiSuggestions.rateLimitNotice}
      resultsAnnouncement={resultsAnnouncement}
      onSelect={handleSelect}
      onClearRecent={clearRecentSearches}
      onDismiss={onDismiss}
      activeIndex={activeIndex}
      className={className}
    />
  );
}
